<template>
    <button
        type="button"
        class="recommendation-card"
        :class="{ 'recommendation-card--overdue': isOverdue, 'recommendation-card--blocked': isBlocked }"
        :disabled="isBlocked"
        @click="$emit('apply', recommendation)"
    >
        <QuantityBadge :value="recommendation.units" :label="unitsLabel" :variant="isOverdue ? 'error' : 'warning'" />

        <span class="recommendation-card__info">
            <span class="recommendation-card__name">{{ productName }}</span>
            <span v-if="isBlocked" class="recommendation-card__blocked">{{ recommendation.blocked_label || $t(`phu_production.blocked.${recommendation.blocked}`) }}</span>
            <span v-else class="recommendation-card__time">
                <span class="recommendation-card__time-value">{{ timeValue }}</span>
                <span v-if="!isOverdue" class="recommendation-card__time-unit">{{ $t('phu_production.sidebar.min') }}</span>
            </span>
        </span>

        <span class="recommendation-card__chevron" aria-hidden="true">
            <i class="i-andy-chevron-right i-andy-xs"></i>
        </span>
    </button>
</template>

<script>
import QuantityBadge from '@/components/domain/ui/QuantityBadge.vue'
import { formatHour, minutesUntil } from '../utils/time'

export default {
    name: 'RecommendationCard',
    components: { QuantityBadge },
    props: {
        recommendation: { type: Object, required: true },
        product: { type: Object, default: null },
        now: { type: Number, required: true }
    },
    computed: {
        unitsLabel() {
            return this.$tc('phu_production.sidebar.units', Number(this.recommendation.units))
        },
        productName() {
            return this.product ? this.product.name : ''
        },
        isOverdue() {
            return this.recommendation.recommended_at <= this.now
        },
        isBlocked() {
            return Boolean(this.recommendation.blocked)
        },
        timeValue() {
            if (this.isOverdue) {
                return formatHour(this.recommendation.recommended_at)
            }
            return String(minutesUntil(this.recommendation.recommended_at, this.now))
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.recommendation-card {
    position: relative;
    isolation: isolate;
    @include display-flex();
    @include align-items(center);
    gap: 16px;
    flex-shrink: 0;
    width: 100%;
    min-height: 70px;
    padding: 8px;
    border: none;
    border-radius: $phu-radius-s;
    background-color: transparent;
    text-align: left;
    cursor: pointer;

    &__info {
        @include display-flex();
        @include flex-direction(column);
        @include justify-content(center);
        flex: 1;
        min-width: 0;
    }

    &__name {
        font-family: $text-bold;
        font-size: 1rem;
        line-height: normal;
        @include text-ellipsis();
        text-transform: uppercase;
        color: $phu-text;
    }

    &__time {
        @include display-flex();
        @include align-items(center);
        gap: 8px;
    }

    &__time-value {
        font-family: $text-bold;
        font-size: 1rem;
        line-height: normal;
        color: $phu-warning;
    }

    &__time-unit {
        font-family: $text;
        font-size: 0.875rem;
        line-height: normal;
        color: $phu-text;
    }

    &__blocked {
        font-family: $text;
        font-size: 0.875rem;
        color: $phu-error;
    }

    &__chevron {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex-shrink: 0;
        padding: 8px;
        border-radius: $phu-radius-s;
        background-color: $phu-button;
        color: $phu-primary;
    }

    &--overdue {
        &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: -1;
            border-radius: $phu-radius-s;
            background-color: $phu-overdue;
            animation: card-pulse 1000ms cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .recommendation-card__time-value {
            color: $phu-contrast-danger;
        }
    }

    &--blocked {
        opacity: 0.6;
        cursor: not-allowed;
    }
}
</style>
